export type InfinexDomain = `https://infinex.xyz${string}`;
export type InfinexSubdomain = `https://${string}.infinex.xyz`;
export type InfinexSupportDomain = `https://support.infinex.xyz${string}`;
export type ExternalRoute =
  | InfinexDomain
  | InfinexSubdomain
  | InfinexSupportDomain;

// Nested routes
type ExternalRouteDefinition =
  | ExternalRoute
  | { readonly [key: string]: ExternalRouteDefinition };

function defineExternalRoutes<
  T extends { [K: string]: ExternalRouteDefinition },
>(routes: T): T {
  return routes;
}

const INFINEX_DOMAIN = 'https://infinex.xyz';

/**
 * Returns true when the URL is populated and points to a different origin
 * than https://infinex.xyz (e.g. external site). Relative paths or same-origin
 * URLs return false so links open in the same tab.
 */
export function isExternalLink(url: string | null | undefined): boolean {
  if (!url?.trim()) return false;
  try {
    const origin = new URL(url, INFINEX_DOMAIN).origin;
    return origin !== INFINEX_DOMAIN;
  } catch {
    return false;
  }
}

export const EXTERNAL_ROUTES = defineExternalRoutes({
  website: INFINEX_DOMAIN,
  getInfinex: `${INFINEX_DOMAIN}/get-infinex`,
  changelog: `${INFINEX_DOMAIN}/news?filter=changelogs`,
  news: `${INFINEX_DOMAIN}/news`,
  legals: {
    termsOfUse: `${INFINEX_DOMAIN}/legals/terms-of-use`,
    privacyPolicy: `${INFINEX_DOMAIN}/legals/privacy-policy`,
    supportedAssets: `${INFINEX_DOMAIN}/legals/supported-assets`,
    fees: `${INFINEX_DOMAIN}/legals/fees`,
    networkCosts: `${INFINEX_DOMAIN}/legals/network-costs`,
    perps: `${INFINEX_DOMAIN}/legals/perps`,
    craterun: `${INFINEX_DOMAIN}/legals/craterun`,
    sale: `${INFINEX_DOMAIN}/legals/infinex-sale`,
  },
  docs: {
    publicPatronNft: `${INFINEX_DOMAIN}/patron-nft`,
  },
  extension: `${INFINEX_DOMAIN}/extension`,
  support: {
    website: 'https://support.infinex.xyz',
    howTo2fa:
      'https://support.infinex.xyz/en/articles/10197732-how-to-set-up-two-factor-authentication-2fa-for-your-infinex-account',
    highPriceImpact:
      'https://support.infinex.xyz/en/articles/10441113-high-price-impact',
    whyDidMySendFail:
      'https://support.infinex.xyz/en/articles/10276995-why-did-my-send-fail',
    whyDidMySwidgeFail:
      'https://support.infinex.xyz/en/articles/10276995-why-did-my-swidge-fail',
    howToLogInToDesktopDeviceUsingYourPhoneTablet:
      'https://support.infinex.xyz/en/articles/11069326-how-to-log-in-to-a-desktop-device-using-your-phone-tablet',
    patronSaleTerms:
      'https://support.infinex.xyz/en/articles/9995592-patron-sale-terms',
    walletRecovery:
      'https://support.infinex.xyz/en/articles/10293672-wallet-recovery',
    pectraUpgrade:
      'https://support.infinex.xyz/en/articles/11424230-pectra-upgrade-what-you-need-to-know',
    howToAddAnAdditionalPasskey:
      'https://support.infinex.xyz/en/articles/11494319-how-to-add-an-additional-passkey',
    howToSendFunds:
      'https://support.infinex.xyz/en/articles/9949821-how-to-send-funds',
    cryptoTerms:
      'https://support.infinex.xyz/en/articles/9719898-crypto-terms#h_cd4c6697b8',
    depositFundsOnHyperliquid:
      'https://support.infinex.xyz/en/articles/12912170-depositing-funds-on-hyperliquid',
  },
  governance: 'https://proposals.infinex.xyz',
});
